import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import {
  pageEntrance,
  heroEntrance,
  fadeInUp,
  staggeredFadeInUp,
} from "../animation";
import { IoIosArrowRoundDown } from "react-icons/io";
import { BsPlusCircleFill } from "react-icons/bs";
import { BlobBackground } from "../components/BlobBackground";
import HomeData from "../data/home.json";
import caseStudies from "../case-studies/data/case-studies";
import { CaseStudyTile } from "../case-studies/components/CaseStudyTile";

const Landing = ({ darkMode, isMobileSafari }) => {
  const [isDesktop, setIsDesktop] = useState(false);

  const updateWidth = () => {
    if (window.innerWidth < 740) {
      setIsDesktop(false);
    } else {
      setIsDesktop(true);
    }
  };

  window.addEventListener("resize", () => {
    updateWidth();
  });

  const fadeMenuBar = () => {
    let windowHeight = window.innerHeight;
    let currScrollPos2 =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    if (currScrollPos2 > windowHeight / 3) {
      document.getElementById("app-header").style.opacity =
        currScrollPos2 / (windowHeight / 3) - 2;
      document.getElementById("app-header").style.display = "flex";
    } else {
      document.getElementById("app-header").style.display = "none";
    }
  };

  useEffect(() => {
    updateWidth();

    let scrollPoint =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    if (scrollPoint < 100) {
      document.getElementById("app-header").style.opacity = 0;
    }
    document.addEventListener("scroll", fadeMenuBar, true);
    return () => {
      document.removeEventListener("scroll", fadeMenuBar, true);
      document.getElementById("app-header").style.opacity = 1;
      document.getElementById("app-header").style.display = "flex";
    };
  }, []);

  return (
    <motion.section {...pageEntrance} className="landing-wrapper">
      <section className="fold">
        <BlobBackground />
        <div className="fold-content-wrapper">
          <div>
            <motion.h1 {...heroEntrance(0)}>Seth Mitchell</motion.h1>
            <motion.h2 {...heroEntrance(0.1)} className="fold-subtitle">
              Product Manager · AI, Platforms & User Experience
            </motion.h2>
            <motion.p {...heroEntrance(0.2)} className="fold-tagline">
              Building products that balance user impact, technical feasibility,
              and business outcomes.
            </motion.p>
          </div>
          <div>
            <motion.span
              {...heroEntrance(0.35)}
              className="above-the-fold-links"
            >
              <Link to="/about">About</Link>
              <Link to="/work">Product Work</Link>
              <Link to="/experience">Experience</Link>
            </motion.span>
          </div>
        </div>

        {isMobileSafari ? (
          <motion.a
            {...heroEntrance(0.6)}
            href="#product-leadership"
            className="fold-arrow safari"
          >
            <IoIosArrowRoundDown />
          </motion.a>
        ) : (
          <motion.a
            {...heroEntrance(0.6)}
            href="#product-leadership"
            className="fold-arrow"
          >
            <IoIosArrowRoundDown />
          </motion.a>
        )}
      </section>
      {HomeData.landingIntroCards.map((card, index) => {
        return (
          <motion.section
            {...staggeredFadeInUp(index)}
            id={card.id}
            className="landing-intro-card"
            key={index}
          >
            {card.id === "product-leadership" ? (
              <>
                <div className="card-text">
                  <h2>
                    {isDesktop
                      ? card.copyLine1
                      : card.mobileCopyLine1 || card.copyLine1}
                  </h2>
                </div>
                <div className="work-imgs product-leadership-imgs">
                  {card.imagesLight.map((img, i) => (
                    <img
                      key={i}
                      src={darkMode ? card.imagesDark[i] : card.imagesLight[i]}
                      alt="Huron"
                    />
                  ))}
                </div>
                <div className="card-text">
                  <h2>
                    {isDesktop
                      ? card.copyLine2
                      : card.mobileCopyLine2 || card.copyLine2}
                  </h2>
                  <div className="card-links">
                    {card.links.map((link, i) => (
                      <Link key={i} to={link.route}>
                        {link.text} <IoIosArrowRoundDown />
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
            {card.id === "true-disc" ? (
              <>
                <div className="card-text">
                  <h2>
                    {isDesktop
                      ? card.copyLine1
                      : card.mobileCopyLine1 || card.copyLine1}
                  </h2>
                </div>
                <div className="true-disc-imgs">
                  <img
                    className="optimist-logo"
                    src={darkMode ? card.imagesDark[0] : card.imagesLight[0]}
                    alt="True DISC"
                  />
                </div>
                <div className="card-text">
                  <h2>
                    {isDesktop
                      ? card.copyLine2
                      : card.mobileCopyLine2 || card.copyLine2}
                  </h2>
                  <Link to={card.linkRoute}>
                    {card.linkText} <IoIosArrowRoundDown />
                  </Link>
                </div>
              </>
            ) : null}
            {card.id === "product-education" ? (
              <>
                <div className="card-text">
                  <h2>
                    {isDesktop
                      ? card.copyLine1
                      : card.mobileCopyLine1 || card.copyLine1}
                  </h2>
                </div>
                <div className="work-imgs product-education-imgs">
                  {card.imagesLight.map((img, i) => (
                    <img
                      key={i}
                      src={darkMode ? card.imagesDark[i] : card.imagesLight[i]}
                    />
                  ))}
                </div>
                <div className="card-text">
                  <h2>
                    {isDesktop
                      ? card.copyLine2
                      : card.mobileCopyLine2 || card.copyLine2}
                  </h2>
                  <Link to={card.linkRoute}>
                    {card.linkText} <IoIosArrowRoundDown />
                  </Link>
                </div>
              </>
            ) : null}
          </motion.section>
        );
      })}
      <section className="landing-project-tiles">
        <motion.h2 {...fadeInUp} className="landing-project-help-text">
          Select a tile to explore a product case study:
        </motion.h2>
        <div className="tiles-container">
          {caseStudies.map((study, index) => (
            <CaseStudyTile key={study.slug} study={study} index={index} />
          ))}
        </div>
      </section>
    </motion.section>
  );
};

export { Landing };
